/* eslint-disable class-methods-use-this */
import { ApolloQueryResult, ObservableQuery } from 'apollo-client';
import { GraphQLError } from 'graphql';
import ApolloService from '../Services/ApolloService';
import { handleApiError } from '../Services/FailApi';
import { ContactStrategy } from '../Services/Request/ContactStrategy';
import { PrivateStrategy } from '../Services/Request/PrivateStrategy';
import { PublicStrategy } from '../Services/Request/PublicStrategy';
import { RequestStrategy } from '../Services/Request/Strategy';

interface ServiceThen<T> {
  data: T;
  serverErrors?: any;
  serverMessage?: string;
}

export interface MutationErrorResponseMessageExtensions {
  attribute: string;
}

export interface MutationErrorResponseMessage<T> {
  data?: T;
  extensions?: MutationErrorResponseMessageExtensions;
  message: string;
}

export type MutationErrorResponse<T> =
  | string
  | Array<MutationErrorResponseMessage<T>>;

export type QueryErrorResponseMessage = ReadonlyArray<GraphQLError> | undefined;

export type QueryErrorResponse = string | QueryErrorResponseMessage;

export default class Interface {
  protected apolloService: ApolloService;

  constructor(contactId?: string, anonymizeRequest?: boolean) {
    const strategy = this.getRequestStrategy(contactId, anonymizeRequest);
    this.apolloService = new ApolloService(strategy);
  }

  protected query<TData, TVariables = any>(
    service: () => ObservableQuery<TData, TVariables>,
    errorMessage: string,
    rejectWithServerResponse = false
  ): Promise<TData> {
    return new Promise<TData>(
      (
        resolve: (data: TData) => void,
        reject: (error: QueryErrorResponse) => void
      ) => {
        const handleError = (errors?: QueryErrorResponseMessage) => {
          if (errors) handleApiError(errors);
          if (rejectWithServerResponse) {
            reject(errors);
          } else {
            reject(errorMessage);
          }
        };
        service().subscribe((response: ApolloQueryResult<TData>) => {
          if (response.errors) {
            handleError(response.errors);
          } else if (
            Object.keys(response.data as Record<string, unknown>).length === 0
          ) {
            handleError();
          } else {
            resolve(response.data);
          }
        }, handleError);
      }
    );
  }

  protected mutate<T>(
    service: () => Promise<ServiceThen<T>>,
    errorFallbackMessage: string,
    rejectWithServerResponse = false
  ): Promise<T> {
    return new Promise<T>(
      (
        resolve: (data: T) => void,
        reject: (error: MutationErrorResponse<T>) => void
      ) => {
        const handleError = (
          serverErrors?: Array<{ message: string }>,
          serverMessage?: string,
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          data?: T
        ) => {
          const message = serverMessage || errorFallbackMessage;

          if (serverErrors) handleApiError(serverErrors);
          if (rejectWithServerResponse && serverErrors) {
            reject(serverErrors);
          } else {
            reject(message);
          }
        };
        service()
          .then((response: ServiceThen<T>) => {
            if (response.serverErrors && response.serverErrors.length > 0) {
              handleError(
                response.serverErrors,
                response.serverMessage,
                response.data
              );
            } else {
              resolve(response.data as T);
            }
          })
          .catch(handleError);
      }
    );
  }

  private getRequestStrategy(
    contactId?: string,
    anonymizeRequest?: boolean
  ): RequestStrategy {
    if (contactId) {
      return new ContactStrategy({ contactId });
    }

    return anonymizeRequest ? new PublicStrategy() : new PrivateStrategy();
  }
}
